import { safeRatio } from '@/services/directOrderService';
import { formatValue, formatWeight, formatNumber, formatPercent, formatPricePerKg } from '@/utils/core/format';

// Column model for the laptop detail grid (RG-18 to RG-21), plus the group aggregation that goes
// with it. Kept out of the component so the focus-driven column set and the ratio aggregation are
// both unit-testable.

/**
 * Service rate of the mobile store cards, the only place the row's serviceRate* fields are shown.
 * Nothing ordered and nothing delivered reads "0 %" there instead of vanishing (#23659); something
 * delivered without any order stays undefined (« — »). The header KPIs keep CL-04 through safeRatio.
 */
function cardServiceRate(delivered, ordered) {
	return !Number(ordered) && !Number(delivered) ? 0 : safeRatio(delivered, ordered);
}

/**
 * Flattens one aggregated store bucket into a grid row.
 *
 * Ratios are carried on the row rather than derived in a valueGetter because the group aggregation
 * has to recompute them from the group's own sums — a valueGetter would leave group rows showing a
 * ratio averaged from the leaves.
 *
 * A delivery location carries `accountId`/`channelId` as plain foreign keys and nothing else — its own
 * `companyName` is the STORE's name, not the account's. Enseigne and Circuit therefore have to be
 * resolved against the account and channel lists the caller already loads for its filters; reading
 * them off the delivery location leaves both columns permanently blank and collapses the RG-22
 * grouping into one empty bucket.
 */
export function toDetailRow(bucket, deliveryLocation = null, { accountById = null, channelById = null } = {}) {
	return {
		id: String(bucket.deliveryLocationId),
		deliveryLocationId: bucket.deliveryLocationId,
		storeName: deliveryLocation?.companyName || deliveryLocation?.label || `#${bucket.deliveryLocationId}`,
		companyName: accountById?.get(deliveryLocation?.accountId)?.name ?? '',
		channelName: channelById?.get(deliveryLocation?.channelId)?.name ?? '',
		orderCount: bucket.orderCount,
		returnOrderCount: bucket.returnOrderCount ?? 0,
		orderedValue: bucket.ordered.value,
		orderedWeight: bucket.ordered.weight,
		deliveredValue: bucket.delivered.value,
		deliveredWeight: bucket.delivered.weight,
		returnedValue: bucket.returned.value,
		returnedWeight: bucket.returned.weight,
		serviceRateValue: cardServiceRate(bucket.delivered.value, bucket.ordered.value),
		serviceRateWeight: cardServiceRate(bucket.delivered.weight, bucket.ordered.weight),
		pricePerKg: bucket.ratios.pricePerKg,
		valuePerOrder: bucket.ratios.valuePerOrder,
		weightPerOrder: bucket.ratios.weightPerOrder,
	};
}

const valueFormatter = (format) => (params) => (params.value == null ? '' : format(params.value));

// A block with no column left (metric hidden by the focus, Commandes/Retours chip off) is dropped
// rather than rendered as an empty group header. The separator between blocks comes from AgGrid.vue,
// which draws it for every column group.
function metricGroup(headerName, children) {
	return children.length > 0 ? { headerName, children } : null;
}

/**
 * `showOrders` / `showReturns` follow the Commandes / Retours chips of the detail block. With the
 * orders off, only the Retours block is left, so the Commandé / Validé / Ratios blocks go (every one of
 * them is built on ordered figures) and the count column counts return orders instead.
 *
 * `storeNameRenderer` is the store column's cell renderer (CompanyName.vue, #23662: opens the store
 * detail), handed in by the builder so this module stays free of .vue imports.
 */
export function buildColumnDefs({ t, showValue = true, showWeight = true, showReturns = false, showOrders = true, storeNameRenderer = null } = {}) {
	const translate = t ?? ((key) => key);

	const orderedChildren = [];
	const deliveredChildren = [];
	const returnChildren = [];
	const ratioChildren = [];

	if (showOrders) {
		if (showWeight) orderedChildren.push({ field: 'orderedWeight', headerName: translate('Weight'), valueFormatter: valueFormatter(formatWeight), aggFunc: 'sum', cellClass: 'ag-right-aligned-cell' });
		if (showValue) orderedChildren.push({ field: 'orderedValue', headerName: translate('Turnover'), valueFormatter: valueFormatter(formatValue), aggFunc: 'sum', cellClass: 'ag-right-aligned-cell' });

		if (showWeight) deliveredChildren.push({ field: 'deliveredWeight', headerName: translate('Weight'), valueFormatter: valueFormatter(formatWeight), aggFunc: 'sum', cellClass: 'ag-right-aligned-cell' });
		if (showValue) deliveredChildren.push({ field: 'deliveredValue', headerName: translate('Turnover'), valueFormatter: valueFormatter(formatValue), aggFunc: 'sum', cellClass: 'ag-right-aligned-cell' });

		if (showValue && showWeight) ratioChildren.push({ field: 'pricePerKg', headerName: translate('PricePerKg'), valueFormatter: valueFormatter(formatPricePerKg), cellClass: 'ag-right-aligned-cell' });
		if (showValue) ratioChildren.push({ field: 'valuePerOrder', headerName: translate('ValuePerOrder'), valueFormatter: valueFormatter(formatValue), cellClass: 'ag-right-aligned-cell' });
		if (showWeight) ratioChildren.push({ field: 'weightPerOrder', headerName: translate('WeightPerOrder'), valueFormatter: valueFormatter(formatWeight), cellClass: 'ag-right-aligned-cell' });
	}

	if (showReturns) {
		if (showWeight) returnChildren.push({ field: 'returnedWeight', headerName: translate('Weight'), valueFormatter: valueFormatter(formatWeight), aggFunc: 'sum', cellClass: 'ag-right-aligned-cell' });
		if (showValue) returnChildren.push({ field: 'returnedValue', headerName: translate('Turnover'), valueFormatter: valueFormatter(formatValue), aggFunc: 'sum', cellClass: 'ag-right-aligned-cell' });
	}

	// PO-01 left the freeze and ordering to us. Frozen: the three identity columns the rep scrolls
	// against; then the order count, which every group header needs; then the metric groups in the
	// mockup's order.
	const columns = [
		{ field: 'storeName', headerName: translate('DeliveryLocation'), pinned: 'left', minWidth: 200, ...(storeNameRenderer ? { cellRenderer: storeNameRenderer, cellRendererParams: ({ data }) => ({ deliveryLocationId: data?.deliveryLocationId }) } : {}) },
		{ field: 'companyName', headerName: translate('Account'), pinned: 'left', minWidth: 140, enableRowGroup: true },
		{ field: 'channelName', headerName: translate('Channel'), pinned: 'left', minWidth: 140, enableRowGroup: true },
		{ field: showOrders ? 'orderCount' : 'returnOrderCount', headerName: translate('OrderCount'), valueFormatter: valueFormatter(formatNumber), aggFunc: 'sum', cellClass: 'ag-right-aligned-cell' },
		metricGroup(translate('Ordered'), orderedChildren),
		metricGroup(translate('Delivered'), deliveredChildren),
		metricGroup(translate('Returns'), returnChildren),
		metricGroup(translate('Ratios'), ratioChildren),
	];

	return columns.filter(Boolean);
}

/**
 * Group aggregation for the detail grid.
 *
 * Two traps this deliberately avoids:
 *  - getGroupRowAgg REPLACES the whole aggregation result for a group, so every plain sum has to be
 *    returned here too. Returning only the ratios blanks the Commandé/Validé/Retours columns on
 *    every group row.
 *  - a group's ratio is a ratio of the group's sums, never the average of its children's ratios.
 *    Averaging would give a small store the same weight as a large one and produce a Prix/Kg that
 *    contradicts the two columns next to it.
 */
export function buildGroupRowAgg() {
	return (params) => {
		const totals = { orderCount: 0, returnOrderCount: 0, orderedValue: 0, orderedWeight: 0, deliveredValue: 0, deliveredWeight: 0, returnedValue: 0, returnedWeight: 0 };

		params.nodes.forEach((node) => {
			const data = node.group ? node.aggData : node.data;
			if (!data) return;
			for (const key of Object.keys(totals)) totals[key] += Number(data[key]) || 0;
		});

		return {
			...totals,
			serviceRateValue: cardServiceRate(totals.deliveredValue, totals.orderedValue),
			serviceRateWeight: cardServiceRate(totals.deliveredWeight, totals.orderedWeight),
			pricePerKg: safeRatio(totals.orderedValue, totals.orderedWeight),
			valuePerOrder: safeRatio(totals.orderedValue, totals.orderCount),
			weightPerOrder: safeRatio(totals.orderedWeight, totals.orderCount),
		};
	};
}

/**
 * Mobile card columns (RG-23).
 *
 * TouchCardMetrics has no notion of a `type: 'currency'` — units come from `formatMetric`, the very
 * formatters the grid columns above already use. The service rate rides on `evoField`, which the card
 * renders in green to the right of the pair.
 *
 * The card prints `fromField` first and `field` second in bold, so the spec's "Commandé → Validé"
 * means field = the DELIVERED figure and fromField = the ordered one, not the other way round.
 *
 * With the Commandes chip off, the card carries the returned figures and the return-order count only,
 * mirroring the laptop grid.
 *
 * Each pair is sortable on both of its figures ("CA commandé" / "CA réel"): sorting on the delivered one
 * alone did nothing visible as long as nothing was delivered yet.
 */
export function buildMobileColumns({ t, showValue = true, showWeight = true, showReturns = false, showOrders = true } = {}) {
	const translate = t ?? ((key) => key);
	const columns = [];
	const orderedLabel = (label) => `${label} ${translate('Ordered').toLowerCase()}`;
	const realLabel = (label) => `${label} ${translate('Real').toLowerCase()}`;

	if (!showOrders) {
		if (showValue) columns.push({ field: 'returnedValue', label: translate('Turnover'), sortLabel: translate('Turnover'), formatMetric: formatValue });
		if (showWeight) columns.push({ field: 'returnedWeight', label: translate('Weight'), sortLabel: translate('Weight'), formatMetric: formatWeight });
		columns.push({ field: 'returnOrderCount', label: translate('OrderCount'), formatMetric: formatNumber });
		return columns;
	}

	if (showValue) columns.push({ field: 'deliveredValue', label: translate('Turnover'), sortLabel: realLabel(translate('Turnover')), fromField: 'orderedValue', fromSortLabel: orderedLabel(translate('Turnover')), formatMetric: formatValue, evoField: 'serviceRateValue', evoFormatMetric: formatPercent });
	if (showWeight) columns.push({ field: 'deliveredWeight', label: translate('Weight'), sortLabel: realLabel(translate('Weight')), fromField: 'orderedWeight', fromSortLabel: orderedLabel(translate('Weight')), formatMetric: formatWeight, evoField: 'serviceRateWeight', evoFormatMetric: formatPercent });
	columns.push({ field: 'orderCount', label: translate('OrderCount'), formatMetric: formatNumber });
	if (showReturns && showValue) columns.push({ field: 'returnedValue', label: translate('Returns'), formatMetric: formatValue });
	if (showValue && showWeight) columns.push({ field: 'pricePerKg', label: translate('PricePerKg'), formatMetric: formatPricePerKg });

	return columns;
}
